import type { RefObject } from "react";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  Link2,
  RemoveFormatting,
} from "lucide-react";

// Barre de mise en forme du composer e-mail (WYSIWYG). Agit sur la sélection de
// l'éditeur contentEditable via document.execCommand — déprécié mais fiable et
// suffisant dans WebKit/Chromium pour gras/italique/souligné/listes/lien.

const COMMANDS = [
  { cmd: "bold", icon: Bold, label: "Gras" },
  { cmd: "italic", icon: Italic, label: "Italique" },
  { cmd: "underline", icon: Underline, label: "Souligné" },
  { cmd: "strikeThrough", icon: Strikethrough, label: "Barré" },
  { sep: true },
  { cmd: "insertUnorderedList", icon: List, label: "Liste à puces" },
  { cmd: "insertOrderedList", icon: ListOrdered, label: "Liste numérotée" },
] as const;

export function EmailToolbar({ editorRef }: { editorRef: RefObject<HTMLDivElement | null> }) {
  const exec = (cmd: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };
  // Garde la sélection : un mousedown sur un bouton volerait le focus à l'éditeur.
  const keepSelection = (e: React.MouseEvent) => e.preventDefault();
  const insertLink = () => {
    // window.prompt fait perdre la sélection de l'éditeur → on la mémorise avant,
    // on la restaure après, puis on crée le lien. Sans sélection, on insère le lien.
    const sel = window.getSelection();
    const range = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    const wasCollapsed = sel?.isCollapsed ?? true;
    const url = window.prompt("Adresse du lien (https://…)");
    if (!url) return;
    editorRef.current?.focus();
    if (range && sel) {
      sel.removeAllRanges();
      sel.addRange(range);
    }
    if (wasCollapsed) {
      const safe = url.replace(/"/g, "%22");
      document.execCommand("insertHTML", false, `<a href="${safe}">${url}</a>`);
    } else {
      document.execCommand("createLink", false, url);
    }
  };
  return (
    <div className="email-toolbar" role="toolbar" aria-label="Mise en forme">
      {COMMANDS.map((c, i) =>
        "sep" in c ? (
          <span key={`sep-${i}`} className="email-toolbar-sep" />
        ) : (
          <button
            key={c.cmd}
            type="button"
            title={c.label}
            aria-label={c.label}
            onMouseDown={keepSelection}
            onClick={() => exec(c.cmd)}
          >
            <c.icon size={15} />
          </button>
        ),
      )}
      <button
        type="button"
        title="Insérer un lien"
        aria-label="Insérer un lien"
        onMouseDown={keepSelection}
        onClick={insertLink}
      >
        <Link2 size={15} />
      </button>
      <button
        type="button"
        title="Effacer la mise en forme"
        aria-label="Effacer la mise en forme"
        onMouseDown={keepSelection}
        onClick={() => exec("removeFormat")}
      >
        <RemoveFormatting size={15} />
      </button>
    </div>
  );
}
